/**
 * TeacherImportPanel — standalone teacher bulk-import (CSV/Excel template,
 * upload, and failed-rows download). Rendered inside the Settings → Imports tab.
 */
import { useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { UploadCloud, FileDown, Upload, AlertTriangle, Users } from "lucide-react";
import { toast } from "sonner";

export default function TeacherImportPanel() {
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [failedRows, setFailedRows] = useState([]);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/teachers/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "teachers_template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };

  const downloadFailedRows = () => {
    if (!failedRows.length) return;
    const cols = ["row", "name", "phone", "subjects", "assignments", "class_teacher_of", "password", "error"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [cols.join(","), ...failedRows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "teacher_import_failed_rows.csv";
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/teachers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const parts = [`${data.created} added`];
      if (data.skipped_duplicates) parts.push(`${data.skipped_duplicates} duplicates skipped`);
      if (data.error_count) parts.push(`${data.error_count} rows had errors`);
      toast.success(parts.join(" · "));
      setFailedRows(data.error_count ? (data.errors || []) : []);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Import failed");
    } finally { setImporting(false); }
  };

  return (
    <div className="max-w-3xl" data-testid="teacher-import-panel">
      <div className="flex items-center gap-2 mb-1 text-sky-700">
        <Users className="h-4 w-4" />
        <span className="text-xs uppercase tracking-[0.2em]">Bulk import teachers</span>
      </div>
      <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>Upload a teacher sheet</h2>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
        Accepts <span className="font-mono">.csv</span> or <span className="font-mono">.xlsx</span>. Required: <span className="font-mono text-slate-700 dark:text-slate-300">name, phone</span>.
        Optional: subjects, assignments, class_teacher_of, password.
      </p>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6">
        <input ref={fileRef} type="file" accept=".csv,.xlsx" onChange={onUpload} className="hidden" data-testid="teacher-import-input" />
        <div
          onClick={() => !importing && fileRef.current?.click()}
          className="cursor-pointer rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/40 hover:bg-slate-50 dark:hover:bg-slate-800 p-8 text-center transition-colors"
          data-testid="teacher-dropzone"
        >
          <div className="h-12 w-12 mx-auto rounded-2xl bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 flex items-center justify-center mb-3">
            <UploadCloud className="h-6 w-6" />
          </div>
          <div className="text-slate-900 dark:text-slate-100 font-medium">{importing ? "Importing…" : "Click to choose a CSV / Excel file"}</div>
          <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">New teachers are matched by phone; duplicates are skipped.</div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={downloadTemplate} className="rounded-full" data-testid="teacher-template-btn">
            <FileDown className="h-4 w-4 mr-1" /> Download template
          </Button>
          <Button onClick={() => fileRef.current?.click()} disabled={importing} className="rounded-full bg-sky-500 hover:bg-sky-600" data-testid="teacher-import-btn">
            <Upload className="h-4 w-4 mr-1" /> {importing ? "Importing…" : "Bulk upload"}
          </Button>
        </div>
      </div>

      {failedRows.length > 0 && (
        <div className="mt-4 flex items-center gap-3 rounded-xl border border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/40 px-4 py-3" data-testid="teacher-import-failed-banner">
          <AlertTriangle className="h-4 w-4 text-rose-500 shrink-0" />
          <div className="flex-1 text-sm text-rose-700 dark:text-rose-300">
            <span className="font-semibold">{failedRows.length}</span> row{failedRows.length === 1 ? "" : "s"} couldn't be imported. Download them, fix the issues, and re-upload.
          </div>
          <Button size="sm" variant="outline" onClick={downloadFailedRows} className="border-rose-300 text-rose-700 hover:bg-rose-100 shrink-0" data-testid="teacher-download-failed-rows-btn">
            <FileDown className="h-4 w-4 mr-1" /> Download failed rows
          </Button>
          <button type="button" onClick={() => setFailedRows([])} className="text-rose-400 hover:text-rose-600 text-lg leading-none px-1" data-testid="teacher-dismiss-failed-banner">×</button>
        </div>
      )}
    </div>
  );
}
